/**
 * CustomerLocationSection - "Customer & Location" form section molecule.
 *
 * Renders the framed section card that wraps the existing
 * CompanyLocationFieldset plus the optional Project Address override
 * input. Used by the JobFormTemplate for both work order and inspection
 * forms so the create experience is identical between the two.
 *
 * Atomic Design: this molecule sits one level above the
 * CompanyLocationFieldset / AddressInput atoms-and-molecules, packaging
 * them into a labeled section that the JobForm organism can drop into
 * its template without owning the section chrome.
 */
import React from 'react';
import { SectionCard } from '../SectionCard';
import { CompanyLocationFieldset } from '../CompanyLocationFieldset';
import { AddressInput } from '../AddressInput';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface CustomerLocationSectionProps {
	/** 'work_order' or 'inspection' — controls section heading copy. */
	formType: 'work_order' | 'inspection';
	/** Currently selected company ID. */
	companyId: string;
	/** Called when the user picks a different company. */
	onCompanyChange: (companyId: string) => void;
	/** Currently selected location ID (filtered by companyId). */
	locationId: string;
	/** Called when the user picks a different location. */
	onLocationChange: (locationId: string) => void;
	/** Project address override — auto-filled from the location, editable. */
	projectAddress: string;
	/** Called when the user types or selects a project-address suggestion. */
	onProjectAddressChange: (address: string) => void;
	/** Inline validation errors keyed by field name. */
	errors?: Record<string, string>;
	/** Standard label CSS class. */
	labelClass: string;
	/** Standard input CSS class. */
	inputClass: string;
	/** Border class for the non-error state. */
	normalBorder: string;
	/** Border class for the error state. */
	errorBorder: string;
	/** When true, disable the Company dropdown (Follow-Up flow locks the
	 *  customer to the parent record's customer). */
	lockCompany?: boolean;
	/** When true, disable the Building / Location dropdown — used by the
	 *  child-WO-under-a-Project create flow so the WO inherits the
	 *  parent project's building. */
	lockLocation?: boolean;
	/** Shows the "Add a New Building" link under the Building dropdown and
	 *  reports the click. The owning organism renders the modal — see
	 *  CompanyLocationFieldsetProps.onAddBuilding. */
	onAddBuilding?: () => void;
	/** Bump to re-fetch the building list after one was created. */
	refreshToken?: number;
	/** Optional extra classes appended to the section root. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const CustomerLocationSection: React.FC<CustomerLocationSectionProps> = ({
	formType,
	companyId,
	onCompanyChange,
	locationId,
	onLocationChange,
	projectAddress,
	onProjectAddressChange,
	errors,
	labelClass,
	inputClass,
	normalBorder,
	errorBorder,
	lockCompany = false,
	lockLocation = false,
	onAddBuilding,
	refreshToken,
	className = '',
}) => {
	/* The heading copy varies per form type — inspections call the
	   physical site a "Building" rather than a "Location". */
	const heading = formType === 'work_order' ? 'Customer & Location' : 'Customer & Building';

	return (
		<SectionCard title={heading} className={`customer-location-section ${className}`} dataRef="molecule-customer-location-section">

			{/* Company + Location fieldset — shared across form types */}
			<CompanyLocationFieldset
				companyId={companyId}
				onCompanyChange={onCompanyChange}
				locationId={locationId}
				onLocationChange={onLocationChange}
				onAddressAutoFill={onProjectAddressChange}
				errors={errors}
				labelClass={labelClass}
				inputClass={inputClass}
				normalBorder={normalBorder}
				errorBorder={errorBorder}
				lockCompany={lockCompany}
				lockLocation={lockLocation}
				onAddBuilding={onAddBuilding}
				refreshToken={refreshToken}
			/>

			{/* Project Address — auto-fills from location, but is editable. */}
			<div className="mt-16px">
				<label htmlFor="projectAddress" className={labelClass}>Project Address</label>
				<AddressInput id="projectAddress" value={projectAddress} onChange={onProjectAddressChange} onSelect={(parsed) => onProjectAddressChange(parsed.street)} placeholder="Start typing project address..." />
				<p className="mt-4px text-12px font-sans text-brand-text-muted">Auto-filled when you select a location or you can override this in the field above.</p>
			</div>
		</SectionCard>
	);
};

export default CustomerLocationSection;
